import { makeStyles, Box } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import InvestorCriteriaPanel from './InvestorCriteriaPanel';
import InvestorIntroPanel from './InvestorIntroPanel';
import { InvestorProfile, ImageProvider, InvestorUpdateBody, InvestorInvestmentCriteria, InvestorOtherCriteria, InvestorIntro, Role } from '../../types/types';
import InvestorOtherCriteriaEdit, { validationSchema } from './InvestorOtherCriteriaPanelEdit';
import InvestorIntroPanelEdit, { unpublishedInvestorIntroValidationSchema } from './InvestorIntroPanelEdit';
import InvestorCriteriaPanelEdit from './InvestorCriteriaPanelEdit';
import InvestorOtherCriteriaPanel from './InvestorOtherCriteriaPanel';
import PublishingGuide from '../../components/PublishingGuide';
import { InvestorProfileElements } from '../../components/PublishingGuide/publishingLogic';
import { useAlert } from '../../contexts';
import { newInvestorCreatedMessage } from './InvestorOnboard';
import { optionalInfo, tbd } from '../../lib/utils';
import { investorPublishedIntroSchema } from '../../lib/validationSchemas';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    width: '100%',
    backgroundColor: theme.palette.background.default,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column'
  },
  paper: {
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2)
    },
    padding: theme.spacing(4)
  },
  topSection: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap'
  }
}));

interface InvestorProfileHolderProps {
  profile?: InvestorProfile | undefined
  profileImageProvider: ImageProvider
  handleUpdate?: (body: InvestorUpdateBody) => Promise<void> | undefined
  renderTopbar: () => React.ReactNode;
}

const InvestorProfileHolder: React.FC<InvestorProfileHolderProps> = ({
  profile,
  profileImageProvider,
  handleUpdate = undefined,
  renderTopbar
}) => {
  const classes = useStyles();
  const [editOtherCriteria, setEditOtherCriteria] = useState<boolean>(false);
  const [editInvestmentCriteria, setEditInvestmentCriteria] = useState<boolean>(false);
  const [editIntro, setEditIntro] = useState<boolean>(false);
  const editable = handleUpdate !== undefined;
  const { message } = useAlert();

  useEffect(() => {
    if (message && message === newInvestorCreatedMessage) {
      setEditIntro(true);
    }
  }, [message]);


  const handleUpdateIntro = async (intro: InvestorIntro) => {
    let castValues = null;
    if (!profile?.isPublished) {
      castValues = unpublishedInvestorIntroValidationSchema.cast(intro) as InvestorIntro;
    } else {
      castValues = investorPublishedIntroSchema.cast(intro) as InvestorIntro;
    }
    if (handleUpdate) {
      await handleUpdate({ intro: castValues });
    }
    setEditIntro(false);
  };

  const handleUpdateInvestmentCriteria = async (investmentCriteria: InvestorInvestmentCriteria) => {
    if (handleUpdate) {
      await handleUpdate({ investmentCriteria });
    }
    setEditInvestmentCriteria(false);
  };

  const handleUpdateOtherCriteria = async (otherCriteria: InvestorOtherCriteria) => {
    const criterion = {
      criteria: otherCriteria
    };
    const castedValues = validationSchema.cast({ otherCriteria: criterion.criteria }) as InvestorUpdateBody;
    console.log('casted values');
    console.log(validationSchema);
    if (handleUpdate) {
      await handleUpdate(castedValues);
    }
    setEditOtherCriteria(false);
  };

  const handlePublishingGuideClick = async (clicked: string) => {
    switch (clicked) {
      case InvestorProfileElements.GeneralInformation:
        setEditIntro(true);
        break;
      case InvestorProfileElements.InvestmentCriteria:
        setEditInvestmentCriteria(true);
        break;
      default:
        break;
    }
  };

  const investorCompanyName = useMemo(() => {
    if (profile) {
      return profile.anonymized ? '' : profile.companyName;
    }
    return 'Company name';
  }, [profile]);

  return (
    <Box className={classes.root}>
      {renderTopbar()}
      {profile && !profile.isPublished && editable && <PublishingGuide profile={profile} role={Role.Investor} handleClick={handlePublishingGuideClick} />}
      <br />
      <InvestorIntroPanel
        investorIntro={{
          companyName: investorCompanyName,
          contactName: profile?.contactName || 'Investor name',
          email: profile?.email || tbd,
          intro: profile?.intro || tbd,
          linkedinProfile: profile?.linkedinProfile || optionalInfo
        }}
        profileImageProvider={profileImageProvider}
        handleEdit={editable ? () => setEditIntro(true) : undefined}
      />
      <br />
      {
          profile && (
            <InvestorCriteriaPanel
              criteria={{
                investmentHigh: profile.investmentHigh,
                investmentLow: profile.investmentLow,
                investmentRevenue: profile.investmentRevenue,
                founderFemale: profile.founderFemale,
                founderUnderrepresented: profile.founderUnderrepresented,
                industryList: profile.industryList,
                geographyList: profile.geographyList,
                marketModelCriteria: profile.marketModelCriteria || { B2B: null, B2C: null, B2G: null, SaaS: null }
              }}
              handleEdit={editable ? () => setEditInvestmentCriteria(true) : undefined}
            />
          )
        }
      {(editable || profile?.otherCriteria)
        && (
        <InvestorOtherCriteriaPanel
          investorOtherCriteria={profile?.otherCriteria || ''}
          handleEdit={editable ? () => setEditOtherCriteria(true) : undefined}
        />
        )}
      { editable
        ? (
          <>
            <InvestorIntroPanelEdit
              open={editIntro}
              handleClose={() => setEditIntro(false)}
              published={(profile && profile.isPublished) || false}
              handleSubmit={handleUpdateIntro}
              investorIntro={{
                contactName: profile?.contactName || '',
                companyName: profile?.companyName || '',
                email: profile?.email || '',
                linkedinProfile: profile?.linkedinProfile || '',
                intro: profile?.intro || ''
              }}
            />
            <InvestorCriteriaPanelEdit
              open={editInvestmentCriteria}
              isPublished={(profile && profile.isPublished) || false}
              handleClose={() => setEditInvestmentCriteria(false)}
              handleSubmit={handleUpdateInvestmentCriteria}
              investorCriteria={{
                investmentHigh: profile?.investmentHigh || 0,
                investmentLow: profile?.investmentLow || 0,
                investmentRevenue: profile?.investmentRevenue || 0,
                founderFemale: profile?.founderFemale || false,
                founderUnderrepresented: profile?.founderUnderrepresented || false,
                industryList: profile?.industryList || [],
                geographyList: profile?.geographyList || [],
                marketModelCriteria: profile?.marketModelCriteria || { B2B: null, B2C: null, B2G: null, SaaS: null }
              }}
            />
            <InvestorOtherCriteriaEdit
              otherCriteria={profile?.otherCriteria || ''}
              open={editOtherCriteria}
              handleClose={() => setEditOtherCriteria(false)}
              handleSubmit={handleUpdateOtherCriteria}
            />
          </>
        )
        : null}
    </Box>
  );
};

export default InvestorProfileHolder;
